import React from 'react';
import Table from '../../components/common/Table';
import { MemberFirearmDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { renderBooleanBadge } from '../../utils/displayUtils';
import { renderCaliberBadge } from '../../utils/firearmDisplayUtils';
import FileIcons from '../common/FileIcons';
import ImagePreview from '../common/ImagePreview';
import { getRoles } from '../../utils/tokenUtils';
import {
  getRenewByInfo,
  getStatus,
  statusLabel,
} from '../../utils/firearmUtils';
import createMemberFirearmActions from '../actionMenus/MemberFirearmActions';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';

interface MemberFirearmTableProps {
  data: MemberFirearmDTO[];
  handleEdit: (row: MemberFirearmDTO) => void;
  handleDownloadPostal: (row: MemberFirearmDTO) => void;
  handleSubmitPostal: (row: MemberFirearmDTO) => void;
  handleDownloadActivity: (row: MemberFirearmDTO) => void;
  handleSubmitActivity: (row: MemberFirearmDTO) => void;
  handleDelete: (row: MemberFirearmDTO) => Promise<void>;
  handleRestore: (row: MemberFirearmDTO) => Promise<void>;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
}

const MemberFirearmTable: React.FC<MemberFirearmTableProps> = ({
  data,
  handleEdit,
  handleDownloadPostal,
  handleSubmitPostal,
  handleDownloadActivity,
  handleSubmitActivity,
  handleDelete,
  handleRestore,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
}) => {
  const roles = getRoles();
  return (
    <Table
      columns={[
        {
          label: 'Image',
          key: 'firearmFileId', // Changed from imagePath
          width: '80px',
          render: (row: MemberFirearmDTO) => (
            <ImagePreview fileId={row.firearmFileId} size="sm" />
          ),
        },

        ...(roles?.includes('Admin')
          ? [
            {
              label: 'Member',
              key: 'member',
              sortable: true,
              sortKey: 'FirstName',
              render: (row: MemberFirearmDTO) => (
                <div className="flex flex-col gap-1">
                  <span>
                    {row.member?.firstName} {row.member?.lastName}
                  </span>
                  <span>{row.member?.idPassportNumber ?? ''}</span>
                </div>
              ),
            },
          ]
          : []),
        {
          label: 'Type',
          key: 'firearmType',
          render: (row: MemberFirearmDTO) => (
            <span>{row.firearmType?.title ?? 'Unknown'}</span>
          ),
        },

        {
          label: 'Make',
          key: 'firearmMake',
          render: (row: MemberFirearmDTO) => {
            const makeTitle = row.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST ? row.firearmMakeOther : row.firearmMake?.title ?? 'Unknown';
            return <span>{makeTitle}</span>;
          },
        },

        {
          label: 'Caliber',
          key: 'caliber',
          render: (row: MemberFirearmDTO) => (
            <>
              {renderCaliberBadge(
                row.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? row.caliberOther : row.caliber?.title,
                row.firearmSpecificId
              ) || <span>Unknown</span>}
            </>
          ),
        },

        {
          label: 'Files',
          key: 'files',
          render: (row: MemberFirearmDTO) => (
            <FileIcons
              files={[
                {
                  id: row.recieptFileId,
                  label: 'Firearm Receipt',
                },
              ]}
            />
          ),
        },
        {
          label: 'Issued',
          key: 'licenceIssueDate',
          sortable: true,
          sortKey: 'LicenceIssueDate',
          format: (value: Date) =>
            convertToUserTime(value?.toString() ?? '', true),
        },
        {
          label: 'Renew By',
          key: 'licenceExpiryDate',
          render: (row: MemberFirearmDTO) => {
            const info = getRenewByInfo(row.licenceExpiryDate);
            return <span className={info.className}>{info.display}</span>;
          },
        },
        {
          label: 'Status',
          key: 'isExpired',
          render: (row: MemberFirearmDTO) => {
            const s = getStatus(row);
            const isGood = s === 'submitted' || s === 'active';
            const falseVariant: 'error' | 'warning' =
              s === 'expired' ? 'error' : 'warning';
            return renderBooleanBadge(
              isGood,
              statusLabel[s],
              statusLabel[s],
              falseVariant,
            );
          },
        },
        {
          label: 'Added',
          key: 'createdUtc',
          sortable: true,
          sortKey: 'CreatedUtc',
          format: (value: Date) =>
            convertToUserTime(value?.toString() ?? '', true),
        },
      ]}
      data={data}
      actions={{
        onEdit: handleEdit,
        extra: (row) =>
          createMemberFirearmActions({
            onDownloadPostalTarget: handleDownloadPostal,
            onSubmitPostalTarget: handleSubmitPostal,
            onDownloadActivityTarget: handleDownloadActivity,
            onSubmitActivityTarget: handleSubmitActivity,
            onDelete: handleDelete,
            onRestore: handleRestore,
          })(row),
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      sortBy={sortBy}
      sortDescending={sortDescending}
      onSort={onSort}
    />
  );
};

export default MemberFirearmTable;
